import type React from "react";
import { useCallback } from "react";
import type { BarSpan, BarTap } from "./DensityChart.lib";
import type { WindowDragStrip } from "./useDensityStrip";
import { useDragDraft } from "./useDragDraft";
import { useWindowKeys } from "./useWindowKeys";
import { useWindowPointer } from "./useWindowPointer";
import { shiftSpan } from "./windowSpan.lib";

interface UseWindowDragParams {
    span: BarSpan;
    barCount: number;
    /** The first bar the window may cover, after a zone before the start. */
    minBar: number;
    strip: WindowDragStrip;
    trackRef: React.RefObject<HTMLElement | null>;
    onChange: (span: BarSpan) => void;
    /** A press on the body that never moved. */
    onBarTap?: (tap: BarTap) => void;
    /** Where a release would land once the window really moves, then null. */
    onPreview?: (span: BarSpan | null) => void;
}

/**
 * Pointer and keyboard handling for a window over a track of bars. The window
 * follows the pointer while dragging, panning the strip near its visible
 * edges, snaps to whole bars on release (`phase` is "settling" so CSS can
 * tween the snap) and commits once. A press on the body that doesn't move is
 * a tap on that bar instead; one on an edge changes nothing, and a cancelled
 * press is dropped. A press on the track outside the window that moves draws
 * a new range ("draw"); `consumeClickAfterDraw` tells the track to skip the click
 * that follows. Arrow keys commit straight away with no tween.
 */
export function useWindowDrag({
    span,
    barCount,
    minBar,
    strip,
    trackRef,
    onChange,
    onBarTap,
    onPreview,
}: UseWindowDragParams) {
    const { unit, firstBar } = strip;
    const previewInStrip = useCallback(
        (preview: BarSpan | null) =>
            onPreview?.(preview && shiftSpan(preview, -firstBar)),
        [firstBar, onPreview],
    );
    const dragDraft = useDragDraft({ unit, onPreview: previewInStrip });
    const { draft, preview, phase, resetDraft } = dragDraft;
    const {
        pointerMode,
        isDragging,
        handlePointerDown,
        handlePointerMove,
        handlePointerEnd,
        handlePointerCancel,
        consumeClickAfterDraw,
    } = useWindowPointer({
        span,
        barCount,
        minBar,
        strip,
        trackRef,
        dragDraft,
        onChange,
        onBarTap,
    });

    const handleKeyDown = useWindowKeys({
        span,
        barCount,
        minBar,
        isDragging,
        onKey: resetDraft,
        onChange,
    });

    const displaySpan = draft ? shiftSpan(draft, -firstBar) : span;
    return {
        displaySpan,
        /** Where a release would land while dragging. */
        periodSpan:
            phase === "dragging" && preview
                ? shiftSpan(preview, -firstBar)
                : displaySpan,
        phase,
        /** The handle or body the pointer holds. */
        pointerMode,
        handlePointerDown,
        handlePointerMove,
        handlePointerEnd,
        handlePointerCancel,
        handleKeyDown,
        consumeClickAfterDraw,
    };
}
